// Banner + Modal para solicitar feedback al cliente post-montaje
import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Heart, MessageCircle, Copy, Loader2, X as IconX, Star, RefreshCw, Trash2,
} from "lucide-react";
import { toast } from "sonner";

export function FeedbackBanner({ peticion, onChange }) {
  const [estado, setEstado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [mensajeExtra, setMensajeExtra] = useState("");
  const [resultado, setResultado] = useState(null);

  // El banner se muestra siempre que el admin pueda usarlo; el admin decide cuándo enviar
  // la encuesta (típicamente tras montaje_completado, pero no nos casamos con el estado del workflow).
  const visible = !!peticion?.id;

  const load = async () => {
    if (!peticion?.id) return;
    try {
      const { data } = await api.get(`/peticiones/${peticion.id}/feedback-status`);
      setEstado(data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [peticion?.id]);

  const solicitar = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/peticiones/${peticion.id}/solicitar-feedback`, {
        mensaje_extra: mensajeExtra.trim() || null,
      });
      setResultado(data);
      toast.success("Enlace de feedback generado");
      await load();
      if (onChange) onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error generando enlace");
    } finally { setLoading(false); }
  };

  const cancelar = async () => {
    if (!confirm("¿Cancelar la petición de feedback? El enlace dejará de funcionar.")) return;
    try {
      await api.post(`/peticiones/${peticion.id}/cancelar-feedback`);
      toast.success("Petición de feedback cancelada");
      setEstado({ token: null, respuesta: null });
      setResultado(null);
      await load();
    } catch (e) {
      toast.error("Error");
    }
  };

  const copyMessage = () => {
    if (!resultado?.mensaje_whatsapp) return;
    navigator.clipboard.writeText(resultado.mensaje_whatsapp);
    toast.success("Mensaje copiado");
  };

  const copyUrl = () => {
    if (!estado?.url) return;
    navigator.clipboard.writeText(estado.url);
    toast.success("URL copiada");
  };

  if (!visible) return null;

  // Ya respondido
  if (estado?.respuesta) {
    const r = estado.respuesta;
    return (
      <div className="mb-6 rounded-xl p-4 border-2 border-emerald-500/40 bg-emerald-500/10" data-testid="banner-feedback-respondido">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-emerald-200 flex items-center gap-2">
              <Heart className="w-4 h-4 fill-pink-400 text-pink-400" />
              Cliente respondió el cuestionario
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-zinc-300 mt-2">
              <span className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className={`w-3 h-3 ${i < (r.satisfaccion || 0) ? "text-amber-400 fill-amber-400" : "text-zinc-700"}`} />
                ))}
              </span>
              <span>NPS <b className={r.nps >= 9 ? "text-emerald-300" : r.nps >= 7 ? "text-amber-300" : "text-rose-300"}>{r.nps}</b>/10</span>
              {r.cumplimos_plazo === true && <span className="text-emerald-300">✅ Plazo OK</span>}
              {r.cumplimos_plazo === false && <span className="text-rose-300">❌ Fuera de plazo</span>}
              {r.autoriza_publicacion && <span className="px-1.5 py-0.5 rounded bg-pink-500/20 text-pink-300 text-[10px] font-semibold">📤 Autoriza publicar</span>}
            </div>
            {r.comentarios && (
              <div className="text-xs italic text-zinc-400 bg-zinc-950/40 rounded p-2 border-l-2 border-emerald-500/40 mt-2">
                "{r.comentarios}"
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Solicitado pero pendiente
  if (estado?.token) {
    return (
      <div className="mb-6 rounded-xl p-4 border-2 border-pink-500/40 bg-pink-500/10" data-testid="banner-feedback-pendiente">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <div className="text-sm font-semibold text-pink-200 flex items-center gap-2">
            <Heart className="w-4 h-4" />
            Cuestionario enviado al cliente · esperando respuesta
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setOpen(true)} variant="outline"
              data-testid="reenviar-feedback-btn"
              className="bg-transparent border-pink-500/40 text-pink-200 hover:bg-pink-500/10">
              <RefreshCw className="w-3 h-3 mr-1.5" /> Re-enviar
            </Button>
            <Button size="sm" onClick={cancelar} variant="outline" data-testid="cancelar-feedback-btn"
              className="bg-transparent border-rose-500/40 text-rose-300 hover:bg-rose-500/10">
              <Trash2 className="w-3 h-3 mr-1.5" /> Cancelar
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <span className="font-mono break-all">{estado.url}</span>
          <Button size="sm" variant="ghost" onClick={copyUrl} className="h-6 px-2" data-testid="copy-feedback-url-btn">
            <Copy className="w-3 h-3" />
          </Button>
          {estado.visto_at && <span className="text-emerald-300 ml-2">· 👁 Visto</span>}
        </div>
        <FeedbackModal
          open={open}
          onClose={() => setOpen(false)}
          mensajeExtra={mensajeExtra}
          setMensajeExtra={setMensajeExtra}
          onSubmit={solicitar}
          loading={loading}
          resultado={resultado}
          onCopy={copyMessage}
        />
      </div>
    );
  }

  // Sin solicitar aún
  return (
    <>
      <div className="mb-6 rounded-xl p-4 border border-pink-500/30 bg-gradient-to-r from-pink-500/5 to-purple-500/5 flex items-center justify-between gap-3 flex-wrap"
           data-testid="banner-solicitar-feedback">
        <div className="text-sm">
          <span className="text-pink-200 font-semibold flex items-center gap-1.5">
            <Heart className="w-4 h-4" /> Trabajo completado
          </span>
          <span className="text-zinc-400 ml-0.5 mt-0.5 inline-block">Pide feedback al cliente y autorización para publicar en RRSS.</span>
        </div>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="solicitar-feedback-btn"
          className="bg-pink-500 hover:bg-pink-400 text-zinc-900 font-bold">
          <Heart className="w-4 h-4 mr-1.5" /> Solicitar feedback
        </Button>
      </div>

      <FeedbackModal
        open={open}
        onClose={() => { setOpen(false); setResultado(null); }}
        mensajeExtra={mensajeExtra}
        setMensajeExtra={setMensajeExtra}
        onSubmit={solicitar}
        loading={loading}
        resultado={resultado}
        onCopy={copyMessage}
      />
    </>
  );
}

function FeedbackModal({ open, onClose, mensajeExtra, setMensajeExtra, onSubmit, loading, resultado, onCopy }) {
  const openWhatsApp = () => {
    if (!resultado?.mensaje_whatsapp) return;
    const text = encodeURIComponent(resultado.mensaje_whatsapp);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md" data-testid="modal-feedback">
        <DialogHeader>
          <DialogTitle className="text-zinc-100 flex items-center gap-2">
            <Heart className="w-5 h-5 text-pink-400" /> Solicitar feedback al cliente
          </DialogTitle>
        </DialogHeader>

        {!resultado ? (
          <div className="space-y-4">
            <p className="text-xs text-zinc-400">
              Generaremos un enlace público para que el cliente rellene un cuestionario corto (NPS, satisfacción, autorización publicación). Tú se lo envías por WhatsApp.
            </p>
            <div>
              <Label className="text-xs text-zinc-400">Mensaje adicional <span className="text-zinc-600">(opcional)</span></Label>
              <Textarea
                value={mensajeExtra}
                onChange={(e) => setMensajeExtra(e.target.value)}
                placeholder="Ej: Tu opinión nos importa mucho para mejorar..."
                rows={2}
                className="bg-zinc-900 border-zinc-700 mt-1"
                data-testid="input-mensaje-extra-feedback"
              />
            </div>
            <Button
              onClick={onSubmit}
              disabled={loading}
              className="w-full bg-pink-500 hover:bg-pink-400 text-zinc-900 font-bold"
              data-testid="confirmar-solicitar-feedback-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Heart className="w-4 h-4 mr-2" />}
              Generar enlace de feedback
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-emerald-300 text-sm flex items-center gap-2">
              <Heart className="w-4 h-4 fill-pink-400 text-pink-400" /> Enlace listo
            </div>
            <div>
              <Label className="text-xs text-zinc-400">URL</Label>
              <div className="font-mono text-xs text-zinc-300 bg-zinc-900 rounded p-2 break-all border border-zinc-800">{resultado.url}</div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Mensaje WhatsApp (listo para copiar)</Label>
              <Textarea
                value={resultado.mensaje_whatsapp}
                readOnly
                rows={7}
                className="bg-zinc-900 border-zinc-700 text-xs mt-1 font-mono"
                data-testid="output-mensaje-whatsapp-feedback"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={onCopy} variant="outline" className="flex-1" data-testid="copy-feedback-whatsapp-btn">
                <Copy className="w-3 h-3 mr-1" /> Copiar
              </Button>
              <Button onClick={openWhatsApp} className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold" data-testid="abrir-whatsapp-feedback-btn">
                <MessageCircle className="w-3 h-3 mr-1" /> Abrir WhatsApp
              </Button>
            </div>
            <Button onClick={onClose} variant="ghost" className="w-full text-zinc-400">
              <IconX className="w-3 h-3 mr-1" /> Cerrar
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
